/** The one conversion site between logical space and screen space (I14).
 *
 * `AdventureCoord` and `PixelCoord` are distinct brands, so a function that
 * wants a cell cannot be handed a pixel and vice versa. Every other module in
 * this package asks for a conversion here rather than multiplying by 64 itself;
 * that is the whole reason the file exists.
 *
 * Two conventions are fixed here and everything downstream depends on them.
 *
 * 1. A world cell `(x, y)` occupies world pixels `[x*T, (x+1)*T)` on each axis.
 *    Its *ground line* - where a sprite standing on it touches the floor - is
 *    the bottom edge, `(y+1)*T`, at the horizontal centre.
 * 2. The terrain display grid is offset half a tile: display cell `(dy, dx)`
 *    spans the square whose corners are the centres of world cells
 *    `(dy-1,dx-1) (dy-1,dx) (dy,dx) (dy,dx-1)`, so its top-left world pixel is
 *    `(dx*T - T/2, dy*T - T/2)`. See `world/dualgrid.ts`.
 *
 * The camera snaps to whole *device* pixels. A camera left at a fractional
 * offset makes every sprite sample half a texel off, and 16-bit pixel art turns
 * to mush - it is the most common way a 2D game gives itself away. The snap
 * quantum is `1 / (zoom * dpr)` world pixels, because that is the world-space
 * size of one device pixel.
 */

import type { AdventureCoord, PixelCoord } from "@game/core";

/** Tile edge in source pixels. Fixed by `TILES/manifest.json`. */
export const TILE_SIZE = 64;
export const HALF_TILE = TILE_SIZE / 2;

export const asAdventureCoord = (n: number): AdventureCoord => (n | 0) as AdventureCoord;
export const asPixelCoord = (n: number): PixelCoord => n as PixelCoord;

/** A cell on the adventure map. `z` is the level, which is not a coordinate the
 *  camera ever interpolates, so it stays a plain number. */
export interface CellPoint {
  readonly x: AdventureCoord;
  readonly y: AdventureCoord;
  readonly z: number;
}

/** A point in world-pixel space, or in screen-pixel space; never mixed with a cell. */
export interface PixelPoint {
  readonly x: PixelCoord;
  readonly y: PixelCoord;
}

export const cellPoint = (x: number, y: number, z = 0): CellPoint =>
  ({ x: asAdventureCoord(x), y: asAdventureCoord(y), z });

export const pixelPoint = (x: number, y: number): PixelPoint =>
  ({ x: asPixelCoord(x), y: asPixelCoord(y) });

/** Half-open rectangle of cells, `x1`/`y1` exclusive. */
export interface CellRect {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

// ---------------------------------------------------------------- cell <-> world pixel

/** Top-left world pixel of a cell. */
export const cellOrigin = (c: CellPoint): PixelPoint =>
  pixelPoint((c.x as number) * TILE_SIZE, (c.y as number) * TILE_SIZE);

/** Centre of a cell, which is where a decal or a flag is pinned. */
export const cellCentre = (c: CellPoint): PixelPoint =>
  pixelPoint((c.x as number) * TILE_SIZE + HALF_TILE, (c.y as number) * TILE_SIZE + HALF_TILE);

/**
 * The ground line of a cell: bottom edge, horizontal centre. Object sprites are
 * placed by matching their frame anchor to this point, and depth-sorted by its
 * `y`, so a nearer object overlaps a farther one.
 */
export const cellGround = (c: CellPoint): PixelPoint =>
  pixelPoint((c.x as number) * TILE_SIZE + HALF_TILE, ((c.y as number) + 1) * TILE_SIZE);

/** World pixel -> the cell containing it. Floors, so negatives land correctly. */
export const worldPixelToCell = (p: PixelPoint, z = 0): CellPoint =>
  cellPoint(Math.floor((p.x as number) / TILE_SIZE), Math.floor((p.y as number) / TILE_SIZE), z);

// ---------------------------------------------------------------- camera

export interface CameraState {
  /** World pixel the viewport is centred on. Not required to be integral. */
  readonly x: number;
  readonly y: number;
  /**
   * Magnification in CSS pixels per world pixel: one of `ZOOM_OUT_STEPS` (a
   * half, three quarters) or a whole number at a device ratio of 1 or more;
   * under a browser zoomed OUT it is the fraction that puts that many DEVICE
   * pixels on each world pixel (1.25 at 80%). See `clampZoom`.
   */
  readonly zoom: number;
  /** Viewport size in CSS pixels. */
  readonly viewW: number;
  readonly viewH: number;
  /** Device pixel ratio the renderer was created with. */
  readonly dpr: number;
}

export interface SnappedCamera extends CameraState {
  /** World pixel drawn at the viewport's top-left, snapped to a device pixel. */
  readonly originX: number;
  readonly originY: number;
  /** World-pixel size of one device pixel: the snap quantum. */
  readonly quantum: number;
}

/**
 * The magnifications below 1 the camera offers, ascending: the adventure
 * map's two "zoom out" steps, a half and three quarters of a device pixel
 * per world pixel.
 *
 * The half is the one minification that can be drawn EXACTLY. `snapCamera`
 * puts the world origin on an even world pixel, so every device pixel covers
 * a 2x2 block of texels and its centre lands on the corner between them; a
 * linear sample there is the mean of the four - a box filter, the downsample
 * a pixel artist would do by hand - and nothing is dropped or doubled.
 *
 * Three quarters is a resample and is offered anyway: four world pixels to
 * three device pixels cannot be exact, so under nearest sampling every fourth
 * row and column would vanish, and under the linear sampling the presenter
 * switches to while minified (`AdventurePresenter.applySampling`) it is a
 * soft 4:3 blend instead - two of every three device pixels a two-texel mix,
 * the third nearer one texel. It is the step between "as authored" and
 * "half", and a player who wants more map without going all the way to half
 * takes the softness over the gap. The atlas carries two texels of clear
 * padding around every frame, so neither sample's reach touches a neighbour.
 *
 * Nothing else below 1: every further fraction is a worse resample with no
 * step it fills.
 */
export const ZOOM_OUT_STEPS: readonly number[] = Object.freeze([0.5, 0.75]);

/** The smallest magnification offered: `ZOOM_OUT_STEPS[0]`. */
export const MIN_ZOOM = 0.5;

/** The largest magnification offered: eight device pixels to a world pixel. */
export const MAX_ZOOM = 8;

/** An ask below this is a request for one of the minified modes. */
const MINIFIED_BELOW = 0.875;

/** A whole number of device pixels per world pixel, 1 to `MAX_ZOOM`. */
const wholeMagnification = (m: number): number =>
  Math.max(1, Math.min(MAX_ZOOM, Math.round(m)));

/** The nearest of `ZOOM_OUT_STEPS` and the whole number nearest `m`. */
const minifiedMagnification = (m: number): number => {
  let best = wholeMagnification(m);
  for (const step of ZOOM_OUT_STEPS) if (Math.abs(step - m) < Math.abs(best - m)) best = step;
  return best;
};

/**
 * The zoom the camera may actually use: one that puts a whole number of
 * device pixels on every world pixel, 1 to `MAX_ZOOM` of them - or one of
 * `ZOOM_OUT_STEPS`, a half or three quarters (see the note there).
 *
 * The ASK decides the mode. Below `MINIFIED_BELOW` the player wants a
 * minified map and gets the step nearest to what the ask comes to in device
 * pixels - three quarters at 1x, and under an 80% browser page both minified
 * modes come to the half, since 0.75 * 0.8 is nearer a half than three
 * quarters. At or above it the map is drawn as authored or larger, and the
 * browser's ratio never pulls it into a fraction: at a device ratio of 1 or
 * more that is a whole CSS zoom - fractional zoom is not offered because it
 * cannot be snapped - and under a browser zoomed OUT the ratio is below 1
 * and a whole CSS zoom is the thing that cannot be snapped: zoom 1 at 0.8 is
 * 0.8 device pixels per texel, and nearest sampling drops every fifth row
 * and column of every tile. So there the zoom is quantised in device pixels
 * instead - `round(zoom * dpr) / dpr`, 1.25 CSS at 80% - and the map reaches
 * the glass at exactly one device pixel per texel, the same art the 100%
 * frame draws, in the room the shrunken chrome gives back.
 */
export const clampZoom = (zoom: number, dpr = 1): number => {
  const ratio = dpr > 0 ? dpr : 1;
  if (zoom < MINIFIED_BELOW) {
    // High-DPR (a phone at 2x/3x) still has room for both minified CSS
    // steps: 0.5 and 0.75 stay more than one device pixel per texel, and
    // they must stay DISTINCT or Zoom Out is a no-op (0.75*3 and 0.5*3
    // both used to snap to 2 device pixels, then divide back to 2/3).
    // The `zoom * ratio` remap is only for a browser zoomed OUT (ratio < 1),
    // where 0.75 * 0.8 is nearer a half and the two steps would otherwise
    // land on a fractional device pixel.
    if (ratio >= 1) return minifiedMagnification(zoom);
    return minifiedMagnification(zoom * ratio) / ratio;
  }
  if (ratio >= 1) return wholeMagnification(zoom);
  return wholeMagnification(zoom * ratio) / ratio;
};

/**
 * Round the camera so that the world origin lands on a whole device pixel.
 *
 * `originX * zoom * dpr` is forced to an integer, which makes the device-pixel
 * position of *every* integral world pixel an integer too - the condition for
 * nearest sampling to hit texel centres.
 */
export function snapCamera(cam: CameraState): SnappedCamera {
  const dpr = cam.dpr > 0 ? cam.dpr : 1;
  const zoom = clampZoom(cam.zoom, dpr);
  const scale = zoom * dpr;
  const rawX = cam.x - cam.viewW / (2 * zoom);
  const rawY = cam.y - cam.viewH / (2 * zoom);
  return {
    ...cam,
    zoom,
    dpr,
    // At a zoom of a half the scale is a half and this rounds the origin to
    // an EVEN world pixel - the alignment the box-filter argument on
    // `ZOOM_OUT_STEPS` depends on. At three quarters the origin lands on a
    // multiple of four thirds; the frame is a resample there either way.
    originX: Math.round(rawX * scale) / scale,
    originY: Math.round(rawY * scale) / scale,
    quantum: 1 / scale,
  };
}

/** World pixel -> screen (CSS) pixel. */
export const worldToScreen = (p: PixelPoint, cam: SnappedCamera): PixelPoint =>
  pixelPoint(((p.x as number) - cam.originX) * cam.zoom, ((p.y as number) - cam.originY) * cam.zoom);

/** Screen (CSS) pixel -> world pixel. */
export const screenToWorld = (p: PixelPoint, cam: SnappedCamera): PixelPoint =>
  pixelPoint((p.x as number) / cam.zoom + cam.originX, (p.y as number) / cam.zoom + cam.originY);

/** Screen pixel -> the cell under it. The pointer path the UI uses. */
export const screenToCell = (p: PixelPoint, cam: SnappedCamera, z = 0): CellPoint =>
  worldPixelToCell(screenToWorld(p, cam), z);

/** Cell -> the screen pixel of its ground line. */
export const cellToScreen = (c: CellPoint, cam: SnappedCamera): PixelPoint =>
  worldToScreen(cellGround(c), cam);

/** The world-pixel rectangle the camera shows. */
export function cameraWorldRect(cam: SnappedCamera): {
  readonly x0: number; readonly y0: number; readonly x1: number; readonly y1: number;
} {
  return {
    x0: cam.originX,
    y0: cam.originY,
    x1: cam.originX + cam.viewW / cam.zoom,
    y1: cam.originY + cam.viewH / cam.zoom,
  };
}

/**
 * Cells the camera can see, grown by `pad` on every side and clipped to the map.
 * `pad` defaults to 1 because a sprite anchored just outside the rect can still
 * reach into it; anything taller than one tile is culled by its own bounds
 * instead (`world/depth.ts`).
 */
export function visibleCellRect(
  cam: SnappedCamera, mapW: number, mapH: number, pad = 1,
): CellRect {
  const r = cameraWorldRect(cam);
  return {
    x0: Math.max(0, Math.floor(r.x0 / TILE_SIZE) - pad),
    y0: Math.max(0, Math.floor(r.y0 / TILE_SIZE) - pad),
    x1: Math.min(mapW, Math.ceil(r.x1 / TILE_SIZE) + pad),
    y1: Math.min(mapH, Math.ceil(r.y1 / TILE_SIZE) + pad),
  };
}

/** Keep the camera centre inside the map so the viewport never leaves the world. */
export function clampCameraToMap(
  cam: CameraState, mapW: number, mapH: number,
): CameraState {
  const zoom = clampZoom(cam.zoom, cam.dpr);
  const halfW = cam.viewW / (2 * zoom);
  const halfH = cam.viewH / (2 * zoom);
  const worldW = mapW * TILE_SIZE;
  const worldH = mapH * TILE_SIZE;
  const x = worldW <= halfW * 2 ? worldW / 2 : Math.max(halfW, Math.min(worldW - halfW, cam.x));
  const y = worldH <= halfH * 2 ? worldH / 2 : Math.max(halfH, Math.min(worldH - halfH, cam.y));
  return { ...cam, zoom, x, y };
}
